import { AxiosInstance } from '@/api/instances';
import { IS_BINARY } from '@/constants';
import { markFeedLinkActivity } from '@/lib/feed-link-activity';
import Dexie, { Table } from 'dexie';

type FeedLinkSlug = 'AT_REAL_TRATO' | 'AT_REAL_BATALIM' | 'AT_REAL_BAT' | 'AT_REAL_TRATOPREV_STT' | 'AT_REAL_ERROR';

export interface FeedLinkError {
  id?: number;
  filename: string;
  name?: string;
  ext?: string;
  error: string;
  slug?: FeedLinkSlug;
}

export interface FeedLinkSuccess {
  id?: number;
  filename: string;
  slug: FeedLinkSlug;
  data: any;
}

export class OfflineFeedLinkDexieService extends Dexie {
  erros!: Table<FeedLinkError>;
  success!: Table<FeedLinkSuccess>;

  isSyncing = false;
  private pauseTimer?: ReturnType<typeof setTimeout>;
  private readonly pauseMs = 5_000;
  private processor?: (body: {
    filename: string;
    slug: FeedLinkSuccess['slug'];
    data: any[];
    error?: string;
  }) => Promise<void>;

  constructor() {
    super('feed:sync-feed-link');
    this.version(1).stores({
      erros: '++id',
      success: '++id, slug'
    });
  }

  // ----------------------------------------------------------------------

  setProcessor(
    processor: (body: { filename: string; slug: FeedLinkSuccess['slug']; data: any[]; error?: string }) => Promise<void>
  ) {
    this.processor = processor;
  }

  setErrorSlugDefault(slug: FeedLinkSuccess['slug']) {
    this.errorSlugDefault = slug;
  }

  setOnProcessed(cb: () => void) {
    this.onProcessed = cb;
  }

  async sync() {
    if (!IS_BINARY) return;
    if (this.isSyncing) return;

    this.isSyncing = true;
    this.scheduleProcessingAfterPause();
    this.isSyncing = false;
  }

  private scheduleProcessingAfterPause() {
    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
    }
    this.pauseTimer = setTimeout(() => this.processAll(), this.pauseMs);
  }

  private lastProcessedWasSuccess = false;
  private errorSlugDefault: FeedLinkSuccess['slug'] = 'AT_REAL_ERROR';
  private onProcessed?: () => void;

  private async processAll() {
    if (this.isSyncing) return;
    this.isSyncing = true;

    try {
      if (!AxiosInstance.defaults.headers.common.Authorization) {
        const [successCount, errorCount] = await Promise.all([this.success.count(), this.erros.count()]);
        if (successCount + errorCount > 0) {
          this.scheduleProcessingAfterPause();
        }
        return;
      }

      let processedAny = false;

      while (true) {
        markFeedLinkActivity();

        const [nextSuccess, nextError] = await Promise.all([
          this.success.orderBy('id').first(),
          this.erros.orderBy('id').first()
        ]);

        const shouldProcessErrorFirst = !!nextError && (this.lastProcessedWasSuccess || !nextSuccess);

        if (shouldProcessErrorFirst) {
          try {
            const slug = nextError!.slug ?? this.errorSlugDefault;

            const body = {
              filename: nextError!.filename,
              slug,
              data: [],
              error: nextError!.error
            };

            if (this.processor) {
              await this.processor(body);
            }

            if (nextError!.id !== undefined) {
              await this.erros.delete(nextError!.id);
            }

            processedAny = true;
            this.lastProcessedWasSuccess = false;
            continue;
          } catch (err) {
            console.error('[feed-link] Falha ao processar erro:', err);
            this.scheduleProcessingAfterPause();
            break;
          }
        }

        if (nextSuccess) {
          try {
            const body = {
              filename: nextSuccess.filename,
              slug: nextSuccess.slug,
              data: Array.isArray(nextSuccess.data) ? nextSuccess.data : [nextSuccess.data],
              error: undefined
            };

            if (this.processor) {
              await this.processor(body);
            }

            if (nextSuccess.id !== undefined) {
              await this.success.delete(nextSuccess.id);
            }

            processedAny = true;
            this.lastProcessedWasSuccess = true;
            continue;
          } catch (err) {
            console.error('[feed-link] Falha ao processar sucesso:', err);
            this.scheduleProcessingAfterPause();
            break;
          }
        }

        // Sem itens para processar
        break;
      }

      if (processedAny && this.onProcessed) {
        this.onProcessed();
      }
    } finally {
      this.isSyncing = false;
    }
  }

  async addSuccess(data: FeedLinkSuccess) {
    if (!IS_BINARY) return;
    markFeedLinkActivity();
    await this.success.add(data);
    this.scheduleProcessingAfterPause();
  }

  async addError(data: FeedLinkError) {
    if (!IS_BINARY) return;
    markFeedLinkActivity();
    await this.erros.add(data);
    this.scheduleProcessingAfterPause();
  }
}
